import {
	Button,
	Descriptions,
	DescriptionsItem,
	Table,
	TableBody,
	TableCell,
	TableRow,
} from "@monbits/ui";
import { ChevronRight } from "lucide-react";
import * as React from "react";
import DrawerForm from "src/components/drawer-form/drawer-form";
import PaymentDescCard from "src/components/payment-desc-card/payment-desc-card";
import PaymentStatusLabel from "src/components/payment-status/payment-status";
import SpinWithMask from "src/components/spin-with-mask/spin-with-mask";
import TimeLabel from "src/components/time-label/time-label";
import customersQueries from "src/queries/customer-queries";
import paymentsQueries from "src/queries/payments-queries";
import { CURRENCY_SYMBOL, type Customer, type Payment } from "src/types";

interface Props {
	data?: Customer;
	openNotificationFunc: (title: string, description: string) => void;
}

const CustomerDescCard: React.FC<Props> = (props: Props) => {
	const getCustomer = customersQueries.getCustomer();
	const getPayment = paymentsQueries.getPayment();
	const [customer, setCustomer] = React.useState<Customer>();
	const [openedCard, changeOpenedCard] = React.useState<Payment[]>([]);
	const [isPaymentDrawerOpen, setIsPaymentDrawerOpen] = React.useState(false);

	const loadPayment = async (orderId: string) => {
		const response = await getPayment.mutateAsync(orderId);

		changeOpenedCard([response]);
		setIsPaymentDrawerOpen(true);
	};

	const loadUser = async () => {
		if (!props.data || props.data.id === "empty") {
			return;
		}

		const response = await getCustomer.mutateAsync(props.data.id);
		setCustomer(response);
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: loadUser is stable
	React.useEffect(() => {
		if (props.data) {
			loadUser();
		}
	}, [props.data]);

	const _changeIsDescFormOpen = (value: boolean) => {
		if (!value) {
			changeOpenedCard([]);
		}
	};

	const isLoading = getCustomer.isPending || getPayment.isPending;

	return (
		<>
			<SpinWithMask isLoading={isLoading} />
			{!isLoading && customer?.details && (
				<div className="space-y-4">
					<Descriptions column={1}>
						<DescriptionsItem label="ID">{customer.id}</DescriptionsItem>

						<DescriptionsItem label="Email">{customer.email}</DescriptionsItem>

						<DescriptionsItem label="Successful Payments">
							{customer.details.successfulPayments}
						</DescriptionsItem>

						{Boolean(customer?.details?.payments.length) && (
							<DescriptionsItem label="Recent payments">
								{null}
							</DescriptionsItem>
						)}
					</Descriptions>

					{Boolean(customer?.details?.payments.length) && (
						<Table>
							<TableBody>
								{customer.details.payments.map((record) => (
									<TableRow key={record.id}>
										<TableCell className="w-[25%]">
											<TimeLabel time={record.createdAt} />
										</TableCell>
										<TableCell>
											<PaymentStatusLabel status={record.status} />
										</TableCell>
										<TableCell className="w-[40%]">
											<span>{`${CURRENCY_SYMBOL[record.currency]} ${record.price}`}</span>
										</TableCell>
										<TableCell className="text-right">
											<Button
												size="sm"
												onClick={(e) => {
													e.stopPropagation();
													loadPayment(record.id);
												}}
											>
												View
												<ChevronRight className="ml-2 h-4 w-4" />
											</Button>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</div>
			)}
			<DrawerForm
				title="Payment details"
				isOpen={isPaymentDrawerOpen}
				onClose={() => setIsPaymentDrawerOpen(false)}
			>
				{openedCard[0] && (
					<PaymentDescCard
						data={openedCard[0]}
						openNotificationFunc={props.openNotificationFunc}
					/>
				)}
			</DrawerForm>
		</>
	);
};

export default CustomerDescCard;
